import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, UserPlus, X } from "lucide-react";
import { roomsApi, type Room } from "@/api/rooms";
import { usersApi, type User } from "@/api/users";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Dialog } from "@/components/ui/Dialog";

export function TenantRooms() {
  const qc = useQueryClient();
  const roomsQ = useQuery({ queryKey: ["all-rooms"], queryFn: roomsApi.listAll });
  const usersQ = useQuery({ queryKey: ["users"], queryFn: usersApi.list });
  const [openNew, setOpenNew] = useState(false);
  const [openMembersFor, setOpenMembersFor] = useState<Room | null>(null);

  const createMut = useMutation({
    mutationFn: (payload: { customer_company_name: string; description?: string }) =>
      roomsApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["all-rooms"] }),
  });

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">Grupos / Conversaciones</h1>
          <Button onClick={() => setOpenNew(true)}>
            <Plus className="size-4" /> Nuevo grupo
          </Button>
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Cliente / Empresa</th>
                <th className="px-4 py-2">Descripción</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {roomsQ.data?.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium">{r.customer_company_name}</td>
                  <td className="px-4 py-2 text-slate-500">{r.description || "—"}</td>
                  <td className="px-4 py-2">{r.status === "active" ? "Activo" : "Archivado"}</td>
                  <td className="px-4 py-2">
                    <Button size="sm" variant="secondary" onClick={() => setOpenMembersFor(r)}>
                      <UserPlus className="size-4" /> Miembros
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <NewRoomDialog
        open={openNew}
        onClose={() => setOpenNew(false)}
        onCreate={(p) => createMut.mutate(p)}
      />
      <MembersDialog
        room={openMembersFor}
        users={usersQ.data ?? []}
        onClose={() => setOpenMembersFor(null)}
      />
    </div>
  );
}

function NewRoomDialog({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (p: { customer_company_name: string; description?: string }) => void;
}) {
  const [form, setForm] = useState({ customer_company_name: "", description: "" });
  return (
    <Dialog open={open} onClose={onClose} title="Nuevo grupo">
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Cliente / Empresa
          </span>
          <Input
            value={form.customer_company_name}
            onChange={(e) => setForm({ ...form, customer_company_name: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Descripción</span>
          <Input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() => {
              onCreate(form);
              setForm({ customer_company_name: "", description: "" });
              onClose();
            }}
          >
            Crear
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function MembersDialog({
  room,
  users,
  onClose,
}: {
  room: Room | null;
  users: User[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const addMut = useMutation({
    mutationFn: (userId: string) => roomsApi.addMember(room!.id, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["all-rooms"] }),
  });

  if (!room) return null;

  return (
    <Dialog open onClose={onClose} title={`Miembros — ${room.customer_company_name}`}>
      <div className="space-y-2">
        <p className="text-sm text-slate-500">Agregá agentes y clientes al grupo:</p>
        {users.map((u) => (
          <div
            key={u.id}
            className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{u.full_name}</div>
              <div className="truncate text-xs text-slate-500">
                {u.email} · {u.role}
              </div>
            </div>
            <Button size="sm" variant="secondary" onClick={() => addMut.mutate(u.id)}>
              Agregar
            </Button>
          </div>
        ))}
        <div className="flex justify-end pt-3">
          <Button variant="ghost" onClick={onClose}>
            <X className="size-4" /> Cerrar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
